import React, { useState, useRef, useEffect } from 'react';
import { 
  MessageSquare, 
  Send, 
  Mic, 
  MicOff, 
  Bot, 
  User, 
  AlertTriangle,
  Clock,
  CheckCircle,
  Volume2,
  VolumeX,
  Shield,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';

// 🔒 医療機関向けセキュアAIサービス
class MedicalSecureAIService {
  constructor() {
    this.isHIPAAMode = true;
    this.apiKey = process.env.REACT_APP_GEMINI_API_KEY;
    this.model = null;
    this.isInitialized = false;
    
    // 🛡️ データ保護フィルター
    this.sensitivePatterns = [
      // 患者個人情報
      /患者.*?[名前|氏名|ID|番号]/gi,
      /[A-Z][a-z]+\s+[A-Z][a-z]+/g, // 人名パターン
      /\d{4}年\d{1,2}月\d{1,2}日/g, // 具体的日付
      /\d{3}-\d{4}-\d{4}/g, // 電話番号
      
      // 具体的な医療情報
      /手術室\s*[A-Z0-9]+/gi,
      /病床\s*[0-9]+/gi,
      /カルテ\s*[番号|No|ID]/gi,
      
      // スタッフ個人情報
      /[田中|佐藤|鈴木|高橋|渡辺|伊藤|山本|中村|小林|加藤]/g, // 一般的な姓
      /Dr\.|医師|看護師.*?[名前|氏名]/gi,
      
      // 具体的数値データ
      /在庫.*?\d+個/gi,
      /場所.*?[A-Z]\-\d+/gi,
    ];

    // 🏥 医療機関向けナレッジベース（匿名化済み）
    this.medicalKnowledgeBase = `
あなたは医療機関向けの専門AIアシスタント「ScrubEdge Medical AI」です。

【重要なセキュリティ原則】
- HIPAA準拠: 患者個人情報は一切取り扱わない
- データ保護: 具体的なスタッフ名、患者名は使用しない
- 匿名化: すべての情報を匿名化して処理
- セキュリティ優先: 不明な場合は「確認が必要」と回答

【専門分野】
- 手術室運営の一般的手順
- 器械管理のベストプラクティス
- 術式別の標準的準備手順
- 在庫管理の原則
- 緊急時対応の標準プロトコル
- 医療安全管理

【応答ガイドライン】
1. 一般的な医療手順のみ説明
2. 具体的な個人・施設情報は避ける
3. 標準的なプロトコルに基づく回答
4. 緊急時は明確で簡潔な指示
5. 不確実な場合は専門医師への相談を推奨

【禁止事項】
- 患者の個人情報への言及
- 具体的なスタッフ名の使用
- 特定施設の機密情報
- 医療診断や治療の具体的指示
`;
  }

  async initialize() {
    if (this.isInitialized) return true;
    
    try {
      const { GoogleGenerativeAI } = await import("@google/generative-ai");
      const genAI = new GoogleGenerativeAI(this.apiKey);
      
      this.model = genAI.getGenerativeModel({ 
        model: "gemini-1.5-flash",
        generationConfig: {
          temperature: 0.3, // より保守的な回答
          topP: 0.8,
          topK: 40,
          maxOutputTokens: 800,
        },
        safetySettings: [
          {
            category: "HARM_CATEGORY_HARASSMENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_HATE_SPEECH", 
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
          {
            category: "HARM_CATEGORY_DANGEROUS_CONTENT",
            threshold: "BLOCK_MEDIUM_AND_ABOVE",
          },
        ],
      });
      
      this.isInitialized = true;
      console.log('🔒 Medical Secure AI initialized');
      return true;
    } catch (error) {
      console.error('❌ Failed to initialize Medical AI:', error);
      return false;
    }
  }

  // 🛡️ 機密データフィルタリング
  filterSensitiveData(text) {
    let filteredText = text;
    let hasFilteredContent = false;

    // センシティブパターンをマスク
    this.sensitivePatterns.forEach(pattern => {
      if (pattern.test(filteredText)) {
        hasFilteredContent = true;
        filteredText = filteredText.replace(pattern, '[機密情報-除去済み]');
      }
    });

    // 数字の連続を部分的にマスク
    filteredText = filteredText.replace(/\d{4,}/g, (match) => {
      hasFilteredContent = true;
      return '[数値データ-保護済み]';
    });

    return {
      filteredText,
      hasFilteredContent,
      originalLength: text.length,
      filteredLength: filteredText.length
    };
  }

  // 🏥 医療機関向け応答生成
  async generateResponse(message, context = {}) {
    if (!this.isInitialized) {
      const initialized = await this.initialize();
      if (!initialized) {
        throw new Error('Medical AI initialization failed');
      }
    }

    try {
      // 🛡️ 入力データの事前フィルタリング
      const filteredInput = this.filterSensitiveData(message);
      
      if (filteredInput.hasFilteredContent) {
        console.warn('⚠️ Sensitive data detected and filtered');
      }

      // 緊急度判定
      const isEmergency = /緊急|急いで|すぐに|至急|エマージェンシー|emergency/i.test(message);
      
      // 医療機関向けプロンプト構築
      const prompt = this.buildMedicalPrompt(filteredInput.filteredText, context, isEmergency);
      
      console.log('📡 Sending secure request to Medical AI...');
      const result = await this.model.generateContent(prompt);
      const response = await result.response;
      let responseText = response.text();

      // 🔒 応答の事後フィルタリング
      const filteredResponse = this.filterSensitiveData(responseText);
      if (filteredResponse.hasFilteredContent) {
        responseText = filteredResponse.filteredText;
        console.warn('⚠️ Sensitive data detected in response and filtered');
      }

      console.log('✅ Secure medical response generated');
      
      return {
        message: responseText,
        confidence: this.calculateMedicalConfidence(responseText, message),
        isEmergency,
        isFiltered: filteredInput.hasFilteredContent || filteredResponse.hasFilteredContent,
        sources: ["Google Gemini AI (Medical-Secure)", "医療標準プロトコル"],
        suggestions: this.generateMedicalSuggestions(message, responseText),
        securityLevel: 'HIPAA-Compliant',
        timestamp: new Date()
      };
      
    } catch (error) {
      console.error('❌ Medical AI Error:', error);
      
      // セキュリティエラーの分類
      if (error.message.includes('SAFETY')) {
        throw new Error('医療安全ガイドラインに基づき、この質問にはお答えできません。');
      } else if (error.message.includes('API_KEY_INVALID')) {
        throw new Error('システム認証エラー。管理者にお問い合わせください。');
      } else {
        throw new Error('医療AIシステムでエラーが発生しました。システム管理者に報告してください。');
      }
    }
  }

  buildMedicalPrompt(message, context, isEmergency) {
    let prompt = this.medicalKnowledgeBase + '\n\n';
    
    prompt += '【現在のセキュリティモード】HIPAA準拠モード - 患者情報保護\n\n';
    
    if (isEmergency) {
      prompt += '🚨 【緊急モード】この質問は緊急性があります。安全で簡潔な標準プロトコルを提供してください。\n\n';
    }
    
    if (context.previousMessages) {
      prompt += '【過去の相談履歴（匿名化済み）】:\n';
      context.previousMessages.slice(-2).forEach((msg, index) => {
        const filtered = this.filterSensitiveData(msg.content);
        prompt += `${msg.type === 'user' ? 'スタッフ' : 'AI'}: ${filtered.filteredText}\n`;
      });
      prompt += '\n';
    }
    
    prompt += `【現在の相談内容】: ${message}\n\n`;
    prompt += '【回答要件】:\n';
    prompt += '- 医療標準プロトコルに基づく一般的な回答\n';
    prompt += '- 患者・スタッフの個人情報は一切含めない\n';
    prompt += '- 具体的な施設名・場所は避ける\n';
    prompt += '- 不明な場合は「専門医師への相談」を推奨\n';
    prompt += '- HIPAA準拠の安全な情報のみ提供\n\n';
    prompt += '医療専門AIとしての安全で有用な回答:';
    
    return prompt;
  }

  calculateMedicalConfidence(response, originalQuestion) {
    let confidence = 60; // 医療情報なので基本的に保守的
    
    // 医療専門用語の適切な使用
    const medicalTerms = ['プロトコル', '標準手順', 'ガイドライン', '安全確認', '専門医', '確認が必要'];
    const termCount = medicalTerms.filter(term => response.includes(term)).length;
    confidence += termCount * 5;
    
    // 安全性への言及
    if (response.includes('安全') || response.includes('確認')) confidence += 10;
    if (response.includes('専門医') || response.includes('医師')) confidence += 10;
    
    // 一般的なプロトコルへの言及
    if (response.includes('標準') || response.includes('プロトコル')) confidence += 5;
    
    // 医療機関では最大90%まで（100%にはしない）
    return Math.min(confidence, 90);
  }

  generateMedicalSuggestions(originalMessage, response) {
    const suggestions = [];
    
    // 安全性関連の提案
    suggestions.push('医療安全プロトコルの確認');
    suggestions.push('標準手順書の参照');
    suggestions.push('専門医師への相談');
    
    // 質問内容に基づく提案
    if (/手術|術式/.test(originalMessage)) {
      suggestions.push('術前チェックリスト', '器械準備標準手順', '安全確認プロトコル');
    } else if (/器械|在庫/.test(originalMessage)) {
      suggestions.push('器械管理ガイドライン', '在庫チェック手順', '発注基準の確認');
    } else if (/緊急/.test(originalMessage)) {
      suggestions.push('緊急時プロトコル', 'スタッフ召集手順', '安全確保手順');
    }
    
    return suggestions.slice(0, 3);
  }

  processSpeechInput(transcript) {
    // 音声認識の医療用語修正
    const medicalCorrections = {
      'ばいぽーら': 'バイポーラ',
      'はーもにっく': 'ハーモニック',
      'とろっかー': 'トロッカー',
      'ふくくうきょう': '腹腔鏡',
      'きかい': '器械',
      'しゅじゅつ': '手術',
      'じゅんび': '準備'
    };

    let correctedText = transcript;
    Object.entries(medicalCorrections).forEach(([wrong, correct]) => {
      correctedText = correctedText.replace(new RegExp(wrong, 'gi'), correct);
    });

    // 🛡️ 音声入力からも機密データをフィルタリング
    const filtered = this.filterSensitiveData(correctedText);
    if (filtered.hasFilteredContent) {
      console.warn('⚠️ Sensitive data detected in speech input and filtered');
    }

    return filtered.filteredText;
  }
}

// 医療機関向けAIサービスのインスタンス
const medicalAI = new MedicalSecureAIService();

const MedicalSecureAIChat = () => {
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'ai',
      content: '🏥 医療機関向けScrubEdge Medical AI です。\n\n【セキュリティモード】HIPAA準拠 - 患者情報保護有効\n\n手術室運営、器械管理、標準プロトコルについてご相談ください。\n\n⚠️ 患者個人情報や機密データは入力しないでください。一般的な手順についてのみお答えいたします。',
      timestamp: new Date(),
      confidence: 100,
      sources: ["ScrubEdge Medical AI"],
      securityLevel: 'HIPAA-Compliant'
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [recognition, setRecognition] = useState(null);
  const [speechSynthesis, setSpeechSynthesis] = useState(null);
  const [aiStatus, setAiStatus] = useState('initializing');
  const [securityMode, setSecurityMode] = useState(true);
  const [showSecurityInfo, setShowSecurityInfo] = useState(false);
  
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Medical AI初期化
  useEffect(() => {
    const initializeMedicalAI = async () => {
      setAiStatus('initializing');
      const success = await medicalAI.initialize();
      setAiStatus(success ? 'ready' : 'error');
    };
    
    initializeMedicalAI();
  }, []);

  // 音声認識の初期化
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognitionInstance = new SpeechRecognition();
      
      recognitionInstance.continuous = false;
      recognitionInstance.interimResults = false;
      recognitionInstance.lang = 'ja-JP';
      
      recognitionInstance.onresult = (event) => {
        const transcript = event.results[0][0].transcript;
        const processedText = medicalAI.processSpeechInput(transcript);
        setInputValue(processedText);
        setIsListening(false);
      };
      
      recognitionInstance.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
      };
      
      recognitionInstance.onend = () => {
        setIsListening(false);
      };
      
      setRecognition(recognitionInstance);
    }

    if ('speechSynthesis' in window) {
      setSpeechSynthesis(window.speechSynthesis);
    }
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // 🔒 セキュアメッセージ送信
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || aiStatus !== 'ready') return;

    // 事前セキュリティチェック
    const preFilterCheck = medicalAI.filterSensitiveData(inputValue);
    
    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date(),
      wasFiltered: preFilterCheck.hasFilteredContent
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsLoading(true);

    try {
      const context = {
        previousMessages: messages.slice(-3) // セキュリティのため履歴を制限
      };
      
      const response = await medicalAI.generateResponse(userMessage.content, context);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: response.message,
        timestamp: new Date(),
        confidence: response.confidence,
        isEmergency: response.isEmergency,
        isFiltered: response.isFiltered,
        sources: response.sources,
        suggestions: response.suggestions,
        securityLevel: response.securityLevel
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // 緊急時の音声読み上げ
      if (response.isEmergency && speechSynthesis) {
        speakMessage(response.message);
      }
    } catch (error) {
      console.error('Medical message send error:', error);
      
      const errorMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: `🔒 医療セキュリティエラー:\n\n${error.message}\n\n安全な質問形式でもう一度お試しください。`,
        timestamp: new Date(),
        confidence: 0,
        isError: true,
        sources: ["Medical Security System"],
        securityLevel: 'HIPAA-Compliant'
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      inputRef.current?.focus();
    }
  };

  const toggleListening = () => {
    if (isListening) {
      recognition?.stop();
      setIsListening(false);
    } else {
      recognition?.start();
      setIsListening(true);
    }
  };

  const speakMessage = (text) => {
    if (!speechSynthesis) return;
    
    if (isSpeaking) {
      speechSynthesis.cancel();
      setIsSpeaking(false);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'ja-JP';
    utterance.rate = 0.9; // 医療情報なのでゆっくり
    utterance.pitch = 1.0;
    
    utterance.onstart = () => setIsSpeaking(true);
    utterance.onend = () => setIsSpeaking(false);
    utterance.onerror = () => setIsSpeaking(false);
    
    speechSynthesis.speak(utterance);
  };

  const handleSuggestionClick = (suggestion) => {
    setInputValue(suggestion);
    inputRef.current?.focus();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const getConfidenceStyle = (confidence) => {
    if (confidence >= 80) return 'text-green-600';
    if (confidence >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getAIStatusDisplay = () => {
    switch (aiStatus) {
      case 'initializing':
        return { text: '医療AI初期化中...', color: 'text-yellow-600', icon: '🔄' };
      case 'ready':
        return { text: 'Medical AI Ready', color: 'text-green-600', icon: '🏥' };
      case 'error':
        return { text: 'Medical AI Error', color: 'text-red-600', icon: '❌' };
      default:
        return { text: 'Unknown', color: 'text-gray-600', icon: '❓' };
    }
  };

  const formatTime = (timestamp) => {
    return timestamp.toLocaleTimeString('ja-JP', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  const statusDisplay = getAIStatusDisplay();

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      {/* 🔒 医療機関向けセキュアヘッダー */}
      <div className="bg-white shadow-sm border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Shield className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">
                ScrubEdge Medical AI
                <Lock className="inline w-5 h-5 text-green-500 ml-2" title="HIPAA Compliant" />
              </h1>
              <p className="text-sm text-gray-600">医療機関向けセキュアAI - HIPAA準拠</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowSecurityInfo(!showSecurityInfo)}
              className="flex items-center space-x-2 text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full"
            >
              {showSecurityInfo ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              <span>セキュリティ情報</span>
            </button>
            <div className="flex items-center space-x-2">
              <span className="text-lg">{statusDisplay.icon}</span>
              <span className={`text-sm ${statusDisplay.color}`}>{statusDisplay.text}</span>
            </div>
          </div>
        </div>
        
        {/* セキュリティ情報パネル */}
        {showSecurityInfo && (
          <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-sm font-semibold text-green-800 mb-2">🔒 セキュリティ保護機能</h3>
            <ul className="text-xs text-green-700 space-y-1">
              <li>✅ HIPAA準拠データ保護</li>
              <li>✅ 患者個人情報自動フィルタリング</li>
              <li>✅ 機密データ送信前除去</li>
              <li>✅ 医療標準プロトコル準拠</li>
              <li>✅ セキュア通信暗号化</li>
            </ul>
          </div>
        )}
      </div>

      {/* メッセージエリア */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
        {messages.map((message) => (
          <div key={message.id} className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
              message.type === 'user' 
                ? 'bg-blue-600 text-white' 
                : message.isError
                ? 'bg-red-50 text-red-800 border border-red-200'
                : message.isEmergency
                ? 'bg-red-50 text-red-800 border border-red-200'
                : 'bg-white text-gray-800 shadow-sm border'
            }`}>
              <div className="flex items-start space-x-2">
                {message.type === 'ai' && (
                  <div className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center ${
                    message.isEmergency ? 'bg-red-500' : 'bg-green-500'
                  }`}>
                    {message.isEmergency ? (
                      <AlertTriangle className="w-4 h-4 text-white" />
                    ) : (
                      <Shield className="w-4 h-4 text-white" />
                    )}
                  </div>
                )}
                {message.type === 'user' && (
                  <User className="flex-shrink-0 w-6 h-6 text-white" />
                )}
                <div className="flex-1">
                  <div className="whitespace-pre-wrap text-sm leading-relaxed">
                    {message.content}
                  </div>
                  
                  {/* セキュリティ警告 */}
                  {message.wasFiltered && (
                    <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-700">
                      🛡️ 機密データが検出され、自動的に保護されました
                    </div>
                  )}
                  
                  {/* AIメッセージの詳細情報 */}
                  {message.type === 'ai' && !message.isError && (
                    <div className="mt-2 pt-2 border-t border-gray-200">
                      <div className="flex items-center justify-between text-xs">
                        <div className="flex items-center space-x-2">
                          <CheckCircle className={`w-3 h-3 ${getConfidenceStyle(message.confidence)}`} />
                          <span className={getConfidenceStyle(message.confidence)}>
                            信頼度: {message.confidence}%
                          </span>
                          {message.securityLevel && (
                            <span className="text-green-600 bg-green-100 px-1 rounded">
                              {message.securityLevel}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="w-3 h-3 text-gray-400" />
                          <span className="text-gray-500">{formatTime(message.timestamp)}</span>
                          {speechSynthesis && (
                            <button
                              onClick={() => speakMessage(message.content)}
                              className="p-1 hover:bg-gray-100 rounded"
                              title="音声で読み上げ"
                            >
                              {isSpeaking ? (
                                <VolumeX className="w-3 h-3 text-gray-400" />
                              ) : (
                                <Volume2 className="w-3 h-3 text-gray-400" />
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                      
                      {message.sources && message.sources.length > 0 && (
                        <div className="text-xs text-gray-500 mt-1">
                          情報源: {message.sources.join(', ')}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* ローディング表示 */}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white text-gray-800 shadow-sm border max-w-xs lg:max-w-md px-4 py-2 rounded-lg">
              <div className="flex items-center space-x-2">
                <Shield className="w-6 h-6 text-green-500" />
                <div className="flex space-x-1">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce"></div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
                </div>
                <span className="text-xs text-gray-500">Medical AI が安全に回答を生成中...</span>
              </div>
            </div>
          </div>
        )}

        {/* 提案表示 */}
        {messages.length > 1 && messages[messages.length - 1].suggestions && !isLoading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 border border-gray-200 rounded-lg p-3 max-w-xs lg:max-w-md">
              <p className="text-xs text-gray-600 mb-2">💡 医療安全な質問例:</p>
              <div className="space-y-1">
                {messages[messages.length - 1].suggestions.map((suggestion, index) => (
                  <button
                    key={index}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="block w-full text-left text-xs bg-white border border-gray-200 rounded px-2 py-1 hover:bg-green-50 hover:border-green-300 transition-colors"
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* 入力エリア */}
      <div className="bg-white border-t px-6 py-4">
        <div className="flex items-end space-x-3">
          <div className="flex-1">
            <textarea
              ref={inputRef}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="医療安全な質問を入力してください（例：手術準備の標準手順、器械管理プロトコル）⚠️ 患者名・スタッフ名は入力禁止"
              className="w-full border border-gray-300 rounded-lg px-4 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
              rows="3"
              disabled={isLoading || aiStatus !== 'ready'}
            />
          </div>
          
          {/* 音声入力ボタン */}
          {recognition && (
            <button
              onClick={toggleListening}
              disabled={isLoading || aiStatus !== 'ready'}
              className={`p-3 rounded-lg transition-colors ${
                isListening 
                  ? 'bg-red-500 text-white hover:bg-red-600' 
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              } disabled:opacity-50`}
              title={isListening ? '音声入力を停止' : '音声入力を開始'}
            >
              {isListening ? <MicOff className="w-5 h-5" /> : <Mic className="w-5 h-5" />}
            </button>
          )}
          
          {/* 送信ボタン */}
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading || aiStatus !== 'ready'}
            className="bg-green-600 text-white p-3 rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            title="安全なメッセージを送信"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
        
        {/* 音声認識状態表示 */}
        {isListening && (
          <div className="mt-2 text-center">
            <span className="text-sm text-red-600 animate-pulse">🎤 音声を聞いています（機密情報は自動フィルタリングされます）...</span>
          </div>
        )}

        {/* セキュリティ状態表示 */}
        <div className="mt-2 text-center">
          <span className="text-xs text-green-600">
            🔒 HIPAA準拠モード有効 - 患者情報保護中
          </span>
        </div>
      </div>
    </div>
  );
};

export default MedicalSecureAIChat;